import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Target,
  Trophy,
  Flame,
  CheckCircle,
  Clock,
  Zap,
  Award,
  RefreshCw,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Challenges() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [weekNumber, setWeekNumber] = useState(1);
  const [history, setHistory] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(`${API}/challenges/current`),
        axios.get(`${API}/challenges/history`)
      ]);
      
      setCurrentChallenge(currentRes.data.challenge);
      setWeekNumber(currentRes.data.week_number);
      setHistory(historyRes.data.challenges || []);
    } catch (error) {
      console.error("Failed to fetch challenges:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshChallenge = async () => {
    setRefreshing(true);
    try {
      // Force generate new challenge by clearing current
      await axios.get(`${API}/challenges/current`);
      fetchChallenges();
      toast.success("Challenge refreshed!");
    } catch (error) {
      toast.error("Failed to refresh challenge");
    } finally {
      setRefreshing(false);
    }
  };

  const getGoalIcon = (goalType) => {
    switch (goalType) {
      case "steps": return "🚶";
      case "workouts": return "💪";
      case "water": return "💧";
      case "sleep": return "😴";
      case "streak": return "🔥";
      default: return "🎯";
    }
  };

  const getProgressPercentage = () => {
    if (!currentChallenge || !currentChallenge.target_value) return 0;
    return Math.min((currentChallenge.progress / currentChallenge.target_value) * 100, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <h1 className="font-bold tracking-tight uppercase">Weekly Challenges</h1>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        {/* Week Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-sm text-center"
        >
          <div className="text-sm text-muted-foreground uppercase tracking-wider">Current</div>
          <div className="text-3xl font-black uppercase tracking-tighter">Week {weekNumber}</div>
        </motion.div>

        {/* Current Challenge */}
        {currentChallenge && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className={`border-2 ${currentChallenge.completed ? 'border-accent bg-accent/5' : 'border-primary bg-primary/5'}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    This Week's Challenge
                  </CardTitle>
                  {currentChallenge.completed && (
                    <div className="flex items-center gap-1 text-accent">
                      <CheckCircle className="h-5 w-5" />
                      <span className="text-sm font-bold uppercase">Complete!</span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4 mb-6">
                  <div className="text-5xl">{getGoalIcon(currentChallenge.goal_type)}</div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-black uppercase tracking-tighter mb-2">
                      {currentChallenge.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {currentChallenge.description}
                    </p>
                  </div>
                </div>
                
                {currentChallenge.target_value && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground uppercase tracking-wider">Progress</span>
                      <span className="font-mono font-bold">
                        {currentChallenge.progress} / {currentChallenge.target_value}
                      </span>
                    </div>
                    <Progress value={getProgressPercentage()} className="h-3" />
                  </div>
                )}
                
                {!currentChallenge.completed && (
                  <p className="text-sm text-muted-foreground text-center">
                    Challenge progress updates automatically based on your activity
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-4 mb-8"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Trophy className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
              <div className="text-2xl font-black font-mono text-yellow-500">
                {history.filter(c => c.completed).length}
              </div>
              <div className="text-xs text-muted-foreground uppercase">Completed</div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Flame className="h-6 w-6 text-orange-500 mx-auto mb-2" />
              <div className="text-2xl font-black font-mono text-orange-500">
                {weekNumber}
              </div>
              <div className="text-xs text-muted-foreground uppercase">Current Week</div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Award className="h-6 w-6 text-primary mx-auto mb-2" />
              <div className="text-2xl font-black font-mono text-primary">
                {history.length > 0 
                  ? Math.round((history.filter(c => c.completed).length / history.length) * 100)
                  : 0}%
              </div>
              <div className="text-xs text-muted-foreground uppercase">Success Rate</div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Past Challenges */}
        {history.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  Past Challenges
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {history.filter(c => c.week_number !== weekNumber).slice(0, 8).map((challenge, i) => (
                    <motion.div
                      key={challenge.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className={`flex items-center gap-4 p-3 rounded-sm ${
                        challenge.completed 
                          ? 'bg-accent/10 border border-accent/30' 
                          : 'bg-secondary/50 border border-border'
                      }`}
                    >
                      <div className="text-2xl">{getGoalIcon(challenge.goal_type)}</div>
                      <div className="flex-1">
                        <div className="font-bold uppercase tracking-tight text-sm">
                          Week {challenge.week_number}: {challenge.title}
                        </div>
                        {challenge.target_value && (
                          <div className="text-xs text-muted-foreground">
                            {challenge.progress} / {challenge.target_value} completed
                          </div>
                        )}
                      </div>
                      {challenge.completed ? (
                        <CheckCircle className="h-5 w-5 text-accent" />
                      ) : (
                        <div className="text-xs text-muted-foreground uppercase">Missed</div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
}
